/* eslint-disable @typescript-eslint/naming-convention */
import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import type { RegulatoryHeader, SiteHeader } from '@ps/web-martech-cms-types';
import { buttonClick as trackButtonClick } from '@ps/web-shared-analytics';
import { Link } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { CMSReferences } from '@swf/cms';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { useRefs } from '@swf/core/providers/RefsProvider';
import { select } from '@swf/core/store';

import useCustomHeader from '../../hooks/useCustomHeader';
import useStickyTopHeaderClassName from '../../hooks/useStickyTopHeaderClassName';

import styles from './CMSHeader.scss';
import AppDownloadBanner from './components/AppDownloadBanner/AppDownloadBanner';
import BrandIconButton from './components/BrandIcon/BrandIconButton';
import BrandIconMobile from './components/BrandIcon/BrandIconMobile';
import BrandLinks from './components/BrandLinks/BrandLinks';
import CMSHeaderMenu from './components/CMSHeaderMenu/CMSHeaderMenu';
import LoginMenu from './components/LoginMenu/LoginMenu';
import RamAccount from './components/RamAccount/RamAccount';
import SelfExclusionTooltip from './components/SelfExclusionTooltip/SelfExclusionTooltip';
import Sidebar from './components/Sidebar/Sidebar';
import TopHeader from './components/TopHeader/TopHeader';

function checkUrl(urls: string[], pathname: string): boolean {
  return urls.some((item: string) => pathname.startsWith(item));
}

const CMSHeader: React.FC<SiteHeader> = ({
  brand_icon,
  brand_icon_url,
  layout = 'Real Money',
  main_navigation,
  secondary_navigation,
  support_navigation,
  regulatory_header
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const topHeaderRef = useRef<HTMLDivElement>(null);
  const { removeRef, setRefs } = useRefs();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => {
    setMenuOpen((prevMenuOpen) => !prevMenuOpen);
    !menuOpen && trackButtonClick({ buttonName: 'Hamburger Menu' });
  }, [menuOpen]);
  const useRam1 = useFeature(FeatureFlag.UseRam1);
  const casinoNonAuth = useFeature(FeatureFlag.CasinoNonAuth);
  const sportsNonAuth = useFeature(FeatureFlag.SportsNonAuth);
  const brand = useSelector(select.cms.getBrandId);
  const site = useSelector(select.cms.getSiteId);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const disableRam = useFeature(FeatureFlag.DisableRam);
  const isMobile = useSelector(select.device.isMobile);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !isEmbedded && !embeddedRoute;

  const DEPanic = useFeature(FeatureFlag.DEPanic);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isSportsSelfExcluded = useSelector(select.account.isSportsSelfExcluded);
  const { lightHeader, prospectHeader, clickableLogoUrl, headerLink, showLocalesDropdown } =
    useCustomHeader();
  const { styles: stickyTopHeaderClassName, isStickyHeader } =
    useStickyTopHeaderClassName('header');
  const { pathname } = useLocation();
  const brandIconUrl = clickableLogoUrl || brand_icon_url;
  const showCustomLightHeader = !lightHeader || (lightHeader && showLocalesDropdown);

  const displayVertical = (url?: string) => {
    switch (true) {
      case url?.includes('/casino/'):
        return casinoNonAuth;
      case url?.includes('/sports/'):
        return sportsNonAuth;
      default:
        return true;
    }
  };

  if (main_navigation?.length) {
    main_navigation = main_navigation.filter((item) => displayVertical(item.url));
  }

  if (secondary_navigation?.length) {
    secondary_navigation = secondary_navigation?.filter(({ show_on_urls, hide_on_urls }) => {
      if (show_on_urls?.length) return checkUrl(show_on_urls, pathname);
      if (hide_on_urls?.length) return !checkUrl(hide_on_urls, pathname);

      return true;
    });
  }

  useEffect(() => {
    setRefs('header', headerRef);
    setRefs('topHeader', topHeaderRef);

    return () => {
      removeRef('header');
      removeRef('topHeader');
    };
  }, [setRefs, removeRef]);

  useEffect(() => {
    if (menuOpen) {
      (document.documentElement as HTMLElement).scrollTop = 0;
    }
  }, [menuOpen]);

  return layout === 'Hidden' ? null : (
    <>
      {showWebContent && <AppDownloadBanner />}
      <header
        ref={headerRef}
        className={classNames(styles.header, stickyTopHeaderClassName, {
          [styles.desktopHeader]: !isMobile
        })}
      >
        <Vary by={[Header.Auth]} />
        <div
          id="sticky-top-container"
          className={classNames({
            [styles.stickyTopContainer]: isStickyHeader,
            [styles.yellowBackground]: site === Sites.FRA
          })}
        >
          <TopHeader
            ref={topHeaderRef}
            regulatory_header={regulatory_header as unknown as RegulatoryHeader[]}
          />
        </div>
        <div
          className={classNames({
            [styles.desktopRamActive]:
              useRam1 && !isMobile && brand !== BrandTypes.FOX_BET && !lightHeader
          })}
        >
          {!lightHeader && (
            <div className={classNames(styles.headerLink, { [styles.hideMobile]: !isMobile })}>
              {site !== Sites.NET && <BrandLinks main={main_navigation} />}
            </div>
          )}
          <div
            className={classNames(styles.menu, {
              [styles.mobileRamActive]: useRam1 && brand !== BrandTypes.FOX_BET && !lightHeader,
              [styles.desktopMenu]: !isMobile,
              [styles.headerMenuHeight]: layout === 'Real Money',
              [styles.headerMenuHeightPlayMoney]: layout === 'Play Money' || lightHeader,
              [styles.showLocalesDropdown]: showLocalesDropdown
            })}
          >
            <div
              className={classNames(styles.menuContent, styles.headerLink, {
                [styles.marginBottom]: brand === BrandTypes.FULL_TILT && !lightHeader
              })}
            >
              <div
                className={classNames(styles.menuContent, styles.desktop, {
                  [styles.showDesktop]: !isMobile
                })}
              >
                <BrandIconButton brandIcon={brand_icon} brandIconUrl={brandIconUrl} />
                {showCustomLightHeader && (
                  <CMSHeaderMenu items={support_navigation} className={styles.cmsMenu} />
                )}
                {DEPanic && isAuthenticated && <SelfExclusionTooltip />}
              </div>
              <span
                className={classNames(styles.menuButton, styles.headerLink, {
                  [styles.hideMenuButton]: !isMobile,
                  [styles.lightHeaderMenuButton]: lightHeader,
                  [styles.customLightHeaderMenuButton]: showLocalesDropdown
                })}
                onClick={toggleMenu}
              >
                {showCustomLightHeader && (
                  <>
                    <span className={styles.burgerSlice} />
                    <span className={styles.burgerSlice} />
                    <span className={styles.burgerSlice} />
                  </>
                )}
                {brandIconUrl && lightHeader && !showLocalesDropdown ? (
                  <Link href={brandIconUrl} track trackText="Brand Icon">
                    <BrandIconMobile brandIcon={brand_icon} />
                  </Link>
                ) : (
                  <BrandIconMobile brandIcon={brand_icon} />
                )}
              </span>
            </div>
            {layout === 'Real Money' && !lightHeader && (
              <>
                <div>
                  {!disableRam && (
                    <div
                      className={classNames(styles.accountLinks, {
                        [styles.extendedAccount]: brand === BrandTypes.FOX_BET
                      })}
                    >
                      {useRam1 ? <RamAccount /> : <LoginMenu />}
                    </div>
                  )}
                </div>
                <div
                  className={classNames(styles.menuContent, styles.desktop, styles.headerLink, {
                    [styles.showDesktop]: !isMobile
                  })}
                >
                  <BrandLinks
                    className={classNames(
                      brand === BrandTypes.FULL_TILT ? styles.marginBottomSm : ''
                    )}
                    main={main_navigation}
                  />
                </div>
              </>
            )}
            {lightHeader && headerLink && <CMSReferences references={headerLink} />}
            {!isSportsSelfExcluded && !lightHeader && !prospectHeader && (
              <div
                className={classNames(styles.menuContent, styles.headerLink, {
                  [styles.desktop]: layout !== 'Play Money',
                  [styles.showDesktop]: layout !== 'Play Money' && !isMobile,
                  [styles.brandedSecondaryMenu]: layout === 'Real Money'
                })}
              >
                <CMSHeaderMenu
                  items={secondary_navigation}
                  className={classNames(styles.cmsMenu, {
                    [styles.noGap]: layout === 'Play Money'
                  })}
                  layout={layout}
                  secondaryMenu
                />
              </div>
            )}
          </div>
          {showCustomLightHeader && (
            <div className={classNames(styles.headerLink, { [styles.hideMobile]: !isMobile })}>
              <Sidebar
                menuOpen={menuOpen}
                toggleMenu={toggleMenu}
                brandIcon={brand_icon}
                brandIconUrl={brandIconUrl}
                main={main_navigation}
                primary={support_navigation}
                secondary={prospectHeader === false ? secondary_navigation : []}
                layout={layout}
              />
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default CMSHeader;
